import { Center, Loader, Paper, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ReactNode, useState } from 'react';
import { CashflowsLayout } from 'src/cashflows/redesign/CashflowsLayout';
import { memberIndex, toGroupMembers } from 'src/cashflows/redesign/useCashflowMembers';
import { ExpenditureRow, IncomeRow, LifeEventRow } from 'src/cashflows/rows';
import { Period } from 'src/cashflows/utils';
import { DataLoadError } from 'src/common/components/data-states/DataLoadError';
import { TabShowsItsOwnEmptyState } from 'src/common/components/data-states/TabShowsItsOwnEmptyState';
import { NoRecordedDataCallout } from 'src/common/components/data-states/NoRecordedDataCallout';
import { FAMILY_CASHFLOWS_SIESTA_PROMPT } from 'src/cashflows/redesign/emptyCashflowsState';
import { JointCashflowModals } from 'src/cashflows/joint/JointCashflowModals';
import {
  buildJointExpenditureRows,
  buildJointIncomeRows,
  buildJointLifeEventRows,
} from 'src/cashflows/joint/owners';
import { useFamilyCashflows } from './useFamilyCashflows';
import { FamilyMember } from './useFamilyMembers';

export function FamilyCashflowsRedesign({
  members,
  modelsSection,
}: {
  members: FamilyMember[];
  modelsSection: ReactNode;
}) {
  const { incomes, expenditures, lifeEvents, isLoading, hasErrors, allFailed, refetch } =
    useFamilyCashflows(members);
  const [period, setPeriod] = useState<Period>('monthly');
  const [incomeOpen, income] = useDisclosure(false);
  const [expenditureOpen, expenditure] = useDisclosure(false);
  const [lifeEventOpen, lifeEvent] = useDisclosure(false);
  const [editedIncome, setEditedIncome] = useState<IncomeRow | undefined>();
  const [editedExpenditure, setEditedExpenditure] = useState<ExpenditureRow | undefined>();
  const [editedLifeEvent, setEditedLifeEvent] = useState<LifeEventRow | undefined>();

  const groupMembers = toGroupMembers(members);
  const byClientId = memberIndex(groupMembers);

  if (isLoading) {
    return (
      <Paper variant="content">
        <Center py="xxl">
          <Loader />
        </Center>
      </Paper>
    );
  }

  // Nothing arrived at all, so there are no totals to qualify — showing the sections now would
  // strike a net position of -£0 against figures nobody has.
  if (allFailed) {
    return (
      <Stack gap="md">
        <DataLoadError
          title="Cashflows could not be loaded"
          subject="the household's"
          what="income, expenditure and life events"
          onRetry={refetch}
          testId="family-cashflows-load-error"
        />
        {modelsSection}
      </Stack>
    );
  }

  const hasAnyCashflows = incomes.length + expenditures.length + lifeEvents.length > 0;
  // One value for both callouts, so the models section stands down exactly when the tab offers
  // the buttons itself. Where a member's read failed, the tab shows the error instead, and the
  // models section keeps its own.
  const showsEmptyState = !hasErrors && !hasAnyCashflows && members.length > 0;

  return (
    <>
      <CashflowsLayout
        period={period}
        onPeriodChange={setPeriod}
        nameMembers
        incomeRows={buildJointIncomeRows(incomes, byClientId, { household: byClientId })}
        expenditureRows={buildJointExpenditureRows(expenditures, byClientId, {
          household: byClientId,
        })}
        lifeEventRows={buildJointLifeEventRows(lifeEvents, byClientId, { household: byClientId })}
        incomeActions={{
          onAdd: () => {
            setEditedIncome(undefined);
            income.open();
          },
          onRowClick: row => {
            setEditedIncome(row);
            income.open();
          },
        }}
        expenditureActions={{
          onAdd: () => {
            setEditedExpenditure(undefined);
            expenditure.open();
          },
          onRowClick: row => {
            setEditedExpenditure(row);
            expenditure.open();
          },
        }}
        lifeEventActions={{
          onAdd: () => {
            setEditedLifeEvent(undefined);
            lifeEvent.open();
          },
          onRowClick: row => {
            setEditedLifeEvent(row);
            lifeEvent.open();
          },
        }}
        banner={
          <FamilyCashflowsBanner
            hasErrors={hasErrors}
            onRetry={refetch}
            showsEmptyState={showsEmptyState}
            members={members}
          />
        }
        modelsSection={
          <TabShowsItsOwnEmptyState value={showsEmptyState}>
            {modelsSection}
          </TabShowsItsOwnEmptyState>
        }
      />

      <JointCashflowModals
        members={groupMembers}
        income={{
          opened: incomeOpen,
          row: editedIncome,
          onClose: () => {
            income.close();
            setEditedIncome(undefined);
          },
        }}
        expenditure={{
          opened: expenditureOpen,
          row: editedExpenditure,
          onClose: () => {
            expenditure.close();
            setEditedExpenditure(undefined);
          },
        }}
        lifeEvent={{
          opened: lifeEventOpen,
          row: editedLifeEvent,
          onClose: () => {
            lifeEvent.close();
            setEditedLifeEvent(undefined);
          },
        }}
      />
    </>
  );
}

/**
 * What sits above the household's figures: a partial failure says the totals may be short and
 * offers the read again, and an empty household gets the two ways to fill it rather than a page
 * of zeros.
 */
function FamilyCashflowsBanner({
  hasErrors,
  onRetry,
  showsEmptyState,
  members,
}: {
  hasErrors: boolean;
  onRetry: () => Promise<unknown>;
  showsEmptyState: boolean;
  members: FamilyMember[];
}) {
  if (hasErrors) {
    return (
      <DataLoadError
        title="Some data could not be loaded"
        onRetry={onRetry}
        testId="family-cashflows-partial-error"
      >
        Cashflow data for one or more family members failed to load, so the totals below may be
        incomplete.
      </DataLoadError>
    );
  }

  if (!showsEmptyState) return null;

  return (
    <NoRecordedDataCallout
      title="No cashflows recorded yet"
      subjects={members.map(member => ({
        clientId: member.clientId,
        firstName: member.client.firstName,
      }))}
      siestaPrompt={FAMILY_CASHFLOWS_SIESTA_PROMPT}
      testId="family-cashflows-empty-state"
    >
      Nothing is recorded for this household yet. Process their documents or ask Siesta to pull in
      the income, expenditure and future life events — or add an item by hand from any section
      below.
    </NoRecordedDataCallout>
  );
}
